import { cn } from "@-/com.npmjs.cn";
import { LinearGradient } from "@-/com.npmjs.expo-linear-gradient";
import { View } from "@-/com.npmjs.react-native";
import {
  cancelAnimation,
  createAnimatedComponent,
  Easing,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withRepeat,
  withTiming,
} from "@-/com.npmjs.react-native-reanimated";
import { withUniwind } from "@-/com.npmjs.uniwind";
import { useTheme } from "@-/dev.improve.haniverse.lib.theme";
import { useVisualActivity } from "@-/dev.improve.haniverse.lib.visual-activity";
import { useEffect } from "react";

const AnimatedView = createAnimatedComponent(View);
const ShimmerGradient = withUniwind(LinearGradient);
const LIGHT_SHIMMER = [
  "rgba(255, 255, 255, 0)",
  "rgba(255, 255, 255, 0.12)",
  "rgba(255, 255, 255, 0)",
] as const;
const DARK_SHIMMER = [
  "rgba(255, 255, 255, 0)",
  "rgba(255, 255, 255, 0.06)",
  "rgba(255, 255, 255, 0)",
] as const;

export type SkeletonProps = React.ComponentProps<typeof View> & {
  /** Keep placeholder geometry without running a shimmer while work is idle. */
  animated?: boolean;
  /**
   * `translucent` (default) is a soft foreground tint that reads the same on `bg-background`,
   * `bg-muted` and `bg-surface`, avoiding a harsh jump when placeholders swap to content.
   */
  variant?: "solid" | "translucent";
};

export function Skeleton({
  animated = true,
  children,
  className,
  onLayout,
  variant = "translucent",
  ...props
}: SkeletonProps & React.RefAttributes<View>) {
  const theme = useTheme();
  const reduceMotion = useReducedMotion();
  const isVisuallyActive = useVisualActivity();
  const showShimmer = animated && !reduceMotion && isVisuallyActive;
  const progress = useSharedValue(0);
  const width = useSharedValue(0);

  useEffect(() => {
    if (!showShimmer) {
      return;
    }
    progress.set(0);
    progress.set(withRepeat(withTiming(1, { duration: 1400, easing: Easing.linear }), -1));
    return () => cancelAnimation(progress);
  }, [progress, showShimmer]);

  const shimmerStyle = useAnimatedStyle(() => ({
    opacity: width.get() > 0 ? 1 : 0,
    transform: [{ translateX: (progress.get() * 2 - 1) * width.get() }],
  }));

  return (
    <View
      className={cn(
        // Loaded tiles, surfaces and inputs are square-cornered (`rounded-xs`); match them by default.
        "relative overflow-hidden rounded-xs",
        variant === "translucent" ? "bg-foreground/12" : "bg-accent",
        className,
      )}
      {...props}
      onLayout={(event) => {
        width.set(event.nativeEvent.layout.width);
        onLayout?.(event);
      }}
    >
      {showShimmer && (
        <AnimatedView
          accessibilityElementsHidden
          className="absolute inset-0"
          importantForAccessibility="no-hide-descendants"
          pointerEvents="none"
          style={shimmerStyle}
          testID="skeleton-shimmer"
        >
          <ShimmerGradient
            className="absolute inset-0"
            colors={theme === "dark" ? DARK_SHIMMER : LIGHT_SHIMMER}
            end={{ x: 1, y: 0.5 }}
            start={{ x: 0, y: 0.5 }}
          />
        </AnimatedView>
      )}
      {children}
    </View>
  );
}
